import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { l413Api, ISODocumentItem, SectionItem } from '../../services/l413Api';
import './L413Form.css';

// ---- Component ----
const L413Form: React.FC = () => {
  const navigate = useNavigate();

  const [keyword, setKeyword]       = useState('');
  const [items, setItems]           = useState<ISODocumentItem[]>([]);
  const [loading, setLoading]       = useState(false);
  const [queried, setQueried]       = useState(false);

  // Section modal state
  const [sectionVisible, setSectionVisible]   = useState(false);
  const [sectionDoc, setSectionDoc]           = useState<ISODocumentItem | null>(null);
  const [sectionItems, setSectionItems]       = useState<SectionItem[]>([]);
  const [sectionLoading, setSectionLoading]   = useState(false);

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await l413Api.queryDocuments({ keyword });
      setItems(res.items || []);
      setQueried(true);
      if ((res.items || []).length === 0) {
        message.info('查無符合條件的ISO文件');
      } else {
        message.success(`查詢到 ${res.items.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => { if (e.key === 'Enter') onQuery(); },
    [onQuery]
  );

  // ---- Open section modal ----
  const openSections = useCallback(async (doc: ISODocumentItem) => {
    setSectionDoc(doc);
    setSectionVisible(true);
    setSectionLoading(true);
    try {
      const res = await l413Api.querySections(doc.fl030_01);
      setSectionItems(res.items || []);
    } catch {
      message.error('載入章節失敗');
      setSectionItems([]);
    } finally {
      setSectionLoading(false);
    }
  }, []);

  // ---- Table columns ----
  const columns: ColumnsType<ISODocumentItem> = [
    {
      title: 'ISO文件編號',
      dataIndex: 'fl030_01',
      key: 'fl030_01',
      width: 130,
      fixed: 'left',
      render: (v: string, record) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => openSections(record)}>
          {v}
        </Button>
      ),
    },
    {
      title: '文件名稱',
      dataIndex: 'fl030_11',
      key: 'fl030_11',
      ellipsis: true,
    },
    {
      title: '條文類別',
      dataIndex: 'fl030_12',
      key: 'fl030_12',
      width: 90,
    },
    {
      title: '版本',
      dataIndex: 'fl030_13',
      key: 'fl030_13',
      width: 80,
    },
    {
      title: '頁次',
      dataIndex: 'fl030_14',
      key: 'fl030_14',
      width: 70,
      align: 'right',
    },
    {
      title: '階層',
      dataIndex: 'fl030_15',
      key: 'fl030_15',
      width: 70,
    },
    {
      title: '修改日期',
      dataIndex: 'fl030_16',
      key: 'fl030_16',
      width: 110,
    },
    {
      title: '章節',
      key: 'sections',
      width: 80,
      align: 'center',
      render: (_: unknown, record) => (
        <Button size="small" onClick={() => openSections(record)}>
          查看
        </Button>
      ),
    },
  ];

  const sectionColumns: ColumnsType<SectionItem> = [
    { title: '序號', dataIndex: 'fl130_02', key: 'fl130_02', width: 70, align: 'center' },
    { title: '章節名稱', dataIndex: 'fl130_11', key: 'fl130_11', width: 160 },
    { title: '章節內容', dataIndex: 'fl130_12', key: 'fl130_12', ellipsis: true },
  ];

  // ---- Render ----
  return (
    <div className="l413-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>文件管理</a>
              <span>/</span>
              <span className="current">ISO正式發行文件查詢</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>ISO正式發行文件查詢</h1>
            <span className="badge">L413</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>關鍵字:</label>
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="ISO文件編號或名稱"
            style={{ width: 240 }}
            allowClear
          />

          <Button type="primary" onClick={onQuery} loading={loading}>
            查詢
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
              </div>
            </div>
          )}

          {queried ? (
            <Table<ISODocumentItem>
              columns={columns}
              dataSource={items}
              rowKey="fl030_01"
              pagination={{ pageSize: 50, showSizeChanger: true }}
              size="small"
              scroll={{ x: 1000, y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">請輸入關鍵字並點擊「查詢」，或直接查詢全部</div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
      </div>

      {/* Section Modal */}
      <Modal
        title={sectionDoc ? `章節明細 — ${sectionDoc.fl030_01} ${sectionDoc.fl030_11}` : '章節明細'}
        open={sectionVisible}
        onCancel={() => { setSectionVisible(false); setSectionItems([]); }}
        footer={null}
        width={800}
      >
        <Table<SectionItem>
          columns={sectionColumns}
          dataSource={sectionItems}
          rowKey="fl130_02"
          loading={sectionLoading}
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
          locale={{ emptyText: '此文件無章節資料' }}
        />
      </Modal>
    </div>
  );
};

export default L413Form;
